/**
 * An SVG with no viewBox scales from its own width/height, and one with
 * neither is drawn at 300×150 and cropped wherever a logo slot sizes it
 * (XN-4554 AC 6). Adding the viewBox on upload fixes it once, for every
 * surface that shows the logo. The drawing itself is never changed.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const PX = /^\s*(\d+(?:\.\d+)?)\s*(?:px\s*)?$/i;

const parse = (text) => {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.namespaceURI !== SVG_NS || root.localName !== 'svg' || doc.querySelector('parsererror')) return null;
  return { doc, root };
};

const lengthOf = (value) => {
  const match = PX.exec(value ?? '');
  return match ? Number(match[1]) : null;
};

// Elements that can style the page, fetch a URL or run. <use>, <a> and <image> stay
// (an image's box is its x/y/width/height; its href is not kept).
const ACTIVE = /^(script|foreignObject|style|feImage|animate|animateMotion|animateTransform|set)$/;
// getBBox needs geometry and text layout, nothing CSS parses (fill, mask,
// cursor, filter... take url() and image-set(), which fetch, escapes and all).
const KEEP = /^(d|x|y|width|height|cx|cy|r|rx|ry|x1|y1|x2|y2|points|transform|viewBox|preserveAspectRatio|display|visibility|text-anchor|dx|dy|id|font-.*|letter-spacing|word-spacing|textLength|lengthAdjust|rotate|startOffset|dominant-baseline|baseline-shift|writing-mode|xml:space)$/;

/** A clone with nothing in it that could run, load or style once it is in the document. */
const sanitisedClone = (root) => {
  const clone = root.cloneNode(true);
  // Allow-list by namespace: XHTML elements (iframe, object, embed...) would
  // become live HTML once the clone is in the document.
  clone.querySelectorAll('*').forEach((node) => {
    // A hidden group is not drawn, so it is not measured; say so before the style attribute goes.
    const hidden = /display\s*:\s*none/i.test(node.getAttribute('style') || '');
    if (node.namespaceURI !== SVG_NS || ACTIVE.test(node.localName) || hidden) node.remove();
  });
  [clone, ...clone.querySelectorAll('*')].forEach((el) => {
    [...el.attributes].forEach((attr) => {
      // A same-document fragment never fetches.
      const fragment = /^(use|textPath)$/.test(el.localName) && attr.localName === 'href' && attr.value.startsWith('#');
      if (!fragment && !KEEP.test(attr.name)) el.removeAttribute(attr.name);
    });
  });
  return clone;
};

/** Measure in the live document, off-screen: getBBox needs layout. */
const measureInDocument = (clone) => {
  const holder = document.createElement('div');
  holder.style.cssText = 'position:absolute;left:-10000px;top:-10000px;visibility:hidden';
  holder.appendChild(clone);
  document.body.appendChild(holder);
  try {
    return typeof clone.getBBox === 'function' ? clone.getBBox() : null;
  } catch (e) {
    return null;
  } finally {
    holder.remove();
  }
};

export function addViewBox(text, measure = measureInDocument) {
  const parsed = parse(text);
  if (!parsed) return null;
  const { root } = parsed;
  if (root.hasAttribute('viewBox')) return null;

  const width = lengthOf(root.getAttribute('width'));
  const height = lengthOf(root.getAttribute('height'));
  let box = null;

  if (width && height) {
    box = { x: 0, y: 0, width, height };
  } else {
    const measured = measure(sanitisedClone(root));
    if (measured && measured.width > 0 && measured.height > 0) box = measured;
  }

  if (!box) return null;
  root.setAttribute('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);
  return new XMLSerializer().serializeToString(root);
}

export async function ensureViewBox(file) {
  try {
    const fixed = addViewBox(await file.text());
    return fixed === null ? file : new File([fixed], file.name, { type: 'image/svg+xml' });
  } catch (e) {
    // Upload unchanged when it can't be fixed.
    return file;
  }
}
